// Functions for fetching and manipulating data

/**
 * Fetch Payroll Info Records from backend for a specific opportunity
 * @param {string} opportunityId - The ID of the opportunity
 * @param {string} hostUrl - The host URL for the API
 * @returns {Promise<{records: Array, status: number}>} - The response data and status
 */
export async function fetchPayrollInfoRecords(opportunityId, hostUrl) {
   try {
      if (!opportunityId) {
         return {
            records: [],
            status: 400,
            error: 'Missing opportunity ID',
         }
      }

      const response = await fetch(
         `${hostUrl}/api/payroll-info?opportunity_id=${opportunityId}`,
      )

      const status = response.status
      const data = await response.json()

      return {
         status,
         records: data.records || [],
         message: data.message,
      }
   } catch (error) {
      console.error('Error fetching Payroll Info Records:', error)
      return {
         status: 500,
         records: [],
         error:
            error.message ||
            'An error occurred while fetching Payroll Info Records',
      }
   }
}

/**
 * Fetch Opportunity Product Records from backend for a specific opportunity
 * @param {string} opportunityId - The ID of the opportunity
 * @param {string} hostUrl - The host URL for the API
 * @returns {Promise<{products: Map<string, number | boolean>, status: number}>} - The response data and status
 */
export async function fetchOpportunityProducts(opportunityId, hostUrl) {
   try {
      if (!opportunityId) {
         return {
            products: new Map(),
            status: 400,
            error: 'Missing opportunity ID',
         }
      }

      const response = await fetch(
         `${hostUrl}/api/opportunity-products?opportunity_id=${opportunityId}`,
      )

      const status = response.status
      const data = await response.json()

      return {
         status,
         products: new Map(data || new Map()),
      }
   } catch (error) {
      console.error('Error fetching Opportunity Product Records:', error)
      return {
         status: 500,
         products: new Map(),
         error:
            error.message ||
            'An error occurred while fetching Opportunity Products',
      }
   }
}

/**
 * Fetch localization strings from backend
 * @param {string} hostUrl - The host URL for the API
 * @returns {Promise<{data: Record<string, Record<string, string>>, status: number, error?: string}>} - The response data and status
 */
export async function fetchStrings(hostUrl) {
   try {
      const response = await fetch(`${hostUrl}/api/strings`)

      const status = response.status
      const data = await response.json()

      return {
         status,
         data: data || {},
      }
   } catch (error) {
      console.error('Error fetching localization strings:', error)
      return {
         status: 500,
         data: {},
         error:
            error.message ||
            'An error occurred while fetching localization strings',
      }
   }
}

/**
 * Fetch feature permissions from backend
 * @param {string} hostUrl - The host URL for the API
 * @returns {Promise<{data: Record<string, string[]>, status: number, error?: string}>} - The feature permissions object with consistent structure
 */
export async function fetchFeaturePermissions(hostUrl) {
   try {
      if (!hostUrl) {
         return {
            status: 400,
            data: {},
            error: 'Missing host URL',
         }
      }

      const response = await fetch(`${hostUrl}/api/feature-permissions`)
      const status = response.status

      if (!response.ok) {
         throw new Error(`Network response was not ok: ${status}`)
      }

      const responseData = await response.json()

      // Ensure we return a consistent structure
      return {
         status,
         data: responseData || {},
         error: responseData?.error,
      }
   } catch (err) {
      console.error('Error fetching feature permissions:', err)
      return {
         status: 500,
         data: {},
         error:
            err.message ||
            'An error occurred while fetching feature permissions',
      }
   }
}

/**
 * Fetch Deal Desk Discount Records from backend for a specific opportunity
 * @param {string} opportunityId - The ID of the opportunity
 * @param {string} hostUrl - The host URL for the API
 * @returns {Promise<{discounts: Array, status: number}>} - The response data and status
 */
export async function fetchDealDeskDiscounts(opportunityId, hostUrl) {
   try {
      if (!opportunityId) {
         return {
            discounts: [],
            status: 400,
            error: 'Missing opportunity ID',
         }
      }

      const response = await fetch(
         `${hostUrl}/api/dealdesk-discount?opportunity_id=${opportunityId}`,
      )

      const status = response.status
      const data = await response.json()

      return {
         status,
         discounts: data.data?.discounts || [],
         message: data.message,
      }
   } catch (error) {
      console.error('Error fetching Deal Desk Discount Records:', error)
      return {
         status: 500,
         discounts: [],
         error:
            error.message ||
            'An error occurred while fetching Deal Desk Discount Records',
      }
   }
}

/**
 * Generate PDF for booked quote (Order Form)
 * @param {Object} state - The quote context state
 * @returns {Promise<void>}
 */
export async function generateBookedQuotePDF(state) {
   try {
      console.log('Generating PDF for booked quote...')
      console.log('State object keys:', Object.keys(state))

      // Use formType 4 for booked quotes (Order Form)
      const formType = 4

      // Sanitize all URL parameters to handle null values
      const sanitizeUrlParam = (value) => {
         if (value === null || value === undefined || value === 'null') {
            return ''
         }
         return value
      }

      // Get host URL from environment
      const host_url = process.env.REACT_APP_HOST_URL ?? ''

      // Get parameters from state and URL
      const urlParams = new URLSearchParams(window.location.search)
      const opportunity_id = state.opportunity_id || state.opportunityId || ''
      const account_name = encodeURIComponent(
         state.accountName || state.account_name || '',
      )
      const opportunity_name = encodeURIComponent(
         state.opportunityName || state.opportunity_name || '',
      )
      const opportunity_contact_name = sanitizeUrlParam(
         state.opportunityContactName ||
            state.opportunity_contact_name ||
            urlParams.get('opportunity_contact_name') ||
            '',
      )
      const opportunity_contact_email = sanitizeUrlParam(
         state.opportunityContactEmail ||
            state.opportunity_contact_email ||
            urlParams.get('opportunity_contact_email') ||
            '',
      )
      const last_agent_email = sanitizeUrlParam(
         state.lastAgentEmail ||
            state.last_agent_email ||
            urlParams.get('last_agent_email') ||
            '',
      )
      const last_agent_name = sanitizeUrlParam(
         state.lastAgentName ||
            state.last_agent_name ||
            urlParams.get('last_agent_name') ||
            '',
      )
      const user_role = sanitizeUrlParam(
         state.userRole ||
            state.user_role ||
            urlParams.get('agent_role') ||
            'Sales',
      )

      // Generate invoice number for Order Form
      const today = new Date()
      const invoice_number = `10${today.getFullYear() % 10}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`

      // Calculate totals from state
      const monthly_cost = state.calculatedValues?.monthlyTotal?.totalPrice || 0
      const due_at_activation =
         state.calculatedValues?.totalDueAtActivation || 0

      // Debug logging - see what's being sent
      console.log('PDF Generation Parameters:', {
         formType,
         opportunity_id,
         account_name: decodeURIComponent(account_name),
         opportunity_name: decodeURIComponent(opportunity_name),
         opportunity_contact_name,
         opportunity_contact_email,
         last_agent_email,
         last_agent_name,
         invoice_number,
         monthly_cost,
         due_at_activation,
         user_role,
         host_url,
      })

      // Call the PDF API - include credentials for authentication
      const response = await fetch(
         `${host_url}/api/pdf?form_type=${formType}&opportunity_id=${opportunity_id}&account_name=${account_name}&opportunity_name=${opportunity_name}&opportunity_contact_name=${opportunity_contact_name}&opportunity_contact_email=${opportunity_contact_email}&last_agent_email=${last_agent_email}&last_agent_name=${last_agent_name}&invoice_number=${invoice_number}&monthly_cost=${monthly_cost}&due_at_activation=${due_at_activation}&user_role=${user_role}`,
         {
            method: 'POST',
            headers: {
               Accept: 'application/json',
               'Content-Type': 'application/json',
            },
            credentials: 'include', // Include cookies for authentication
            body: JSON.stringify(state.calculatedValues || {}),
         },
      )

      if (response.ok) {
         console.log('Order Form PDF generated successfully')
         return { success: true }
      } else {
         const errorText = await response.text()
         console.error('Failed to generate Order Form PDF:', errorText)

         // Sanitize error message to remove access_token from URL
         let sanitizedError = errorText
         if (sanitizedError.includes('access_token=')) {
            sanitizedError = sanitizedError.replace(
               /access_token=[^&\s]+/g,
               'access_token=[REDACTED]',
            )
         }

         throw new Error(`PDF generation failed: ${sanitizedError}`)
      }
   } catch (error) {
      console.error('Error generating booked quote PDF:', error)

      // Sanitize error message to remove access_token if present
      let errorMessage = error.message || error.toString()
      if (errorMessage.includes('access_token=')) {
         errorMessage = errorMessage.replace(
            /access_token=[^&\s]+/g,
            'access_token=[REDACTED]',
         )
      }

      const sanitizedError = new Error(errorMessage)
      sanitizedError.stack = error.stack
      throw sanitizedError // Re-throw to let BookQuoteButton handle the error
   }
}
